/**
 * @render react
 * @name Search
*/

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Input } from 'Components/atoms/Input';
import { Form } from 'Components/molecules/Form';
import { Label } from 'Components/molecules/Label';

/**
 * Search component
 *
 * @param {string} label - label to be used for screen readers
 * @param {string} placeholder - text to be displayed on the input
 * @param {string} url - url for redirect. With trailing slash
 * @param {Function} onSubmit - callback function to be invoked on component submit
 * @param {string} submitLabel - Label for the submit button
 * @param {string} inputId - ID for the search input field
 * @param {string} inputName - Name for the search input field
 * @param {string} srLabel - Screen reader label to pass to the button in case the label is not provided
 * @param {Object} context - extra information coming from Polaris provider
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - extra classes to be applied to the component
 */
class Search extends PureComponent {
  /**
   * Constructor
   */
  constructor(props) {
    super(props);
    const { currentFilter, searchTerm } = this.props;

    this.state = {
      searchTerm,
      currentFilter,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
  }

  handleInputChange(e) {
    e.preventDefault();
    const { target: { value } } = e;

    this.setState({
      searchTerm: value,
    });
  }

  handleFilterChange(e) {
    e.preventDefault();
    const { target: { value } } = e;

    this.setState({
      currentFilter: value,
    }, () => {
      this.queryResults(false);
    });
  }

  handleFormSubmit(e) {
    e.preventDefault();

    this.queryResults(false);
  }

  queryResults(shouldDebounce = true) {
    const { onSubmit } = this.props;
    const { searchTerm, currentFilter } = this.state;

    if (onSubmit) {
      if (typeof window !== 'undefined') {
        window.dataLayer.push({
          event: 'searchbar.button_click',
          search_phrase: searchTerm,
          search_placement: 'results page',
        });
      }

      onSubmit(searchTerm, currentFilter, shouldDebounce);
    }
  }

  render() {
    const {
      label,
      labelScreenReaderText,
      placeholder,
      url,
      submitLabel,
      srLabel,
      showFilters,
      filters,
      filtersLabel,
      inputId,
      inputName,
      context: { cssPrefix },
      loadStyles,
      extraClassNames,
    } = this.props;

    const {
      currentFilter,
      searchTerm,
    } = this.state;

    return (
      <div
        className={classNames({
          [`${cssPrefix}__search`]: loadStyles,
          '-with-filters': showFilters,
          ...extraClassNames,
        })}
      >
        <Form
          action={url}
          onSubmit={this.handleFormSubmit}
          extraClassNames={{ [`${cssPrefix}__search--form`]: true }}
          role="search"
        >
          <Label
            content={label}
            target={inputId || `${cssPrefix}__search--input`}
            className={`${cssPrefix}__search--label`}
          >
            {labelScreenReaderText && (
              <span className="screen-reader-text">{labelScreenReaderText}</span>
            )}
          </Label>
          <Input
            id={inputId || `${cssPrefix}__search--input`}
            placeholder={placeholder}
            onChange={this.handleInputChange}
            name={inputName}
            type="search"
            value={searchTerm}
            srLabel={srLabel}
          />
          <Button type="submit" label={submitLabel} srLabel={srLabel} disabled={searchTerm.length === 0} />
        </Form>

        {showFilters && searchTerm.length > 0 && (
          <div className={`${cssPrefix}__search--filters`}>
            <span className={`${cssPrefix}__search--filter-label`}>{filtersLabel}</span>

            <ul>
              {filters.map(filter => (
                <li key={filter.id}>
                  <input
                    id={filter.id}
                    type="radio"
                    name="filters"
                    checked={currentFilter === filter.id}
                    onChange={this.handleFilterChange}
                    value={filter.id}
                  />
                  <Label content={filter.label} target={filter.id} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
}

/**
 * Valid prop types
 */
Search.propTypes = {
  label: PropTypes.string,
  labelScreenReaderText: PropTypes.string,
  placeholder: PropTypes.string,
  url: PropTypes.string,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
  inputId: PropTypes.string,
  inputName: PropTypes.string,
  srLabel: PropTypes.string,
  showFilters: PropTypes.bool,
  filters: PropTypes.arrayOf(PropTypes.shape()),
  filtersLabel: PropTypes.string,
  currentFilter: PropTypes.string,
  searchTerm: PropTypes.string,
  context: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.instanceOf(Object),
};

/**
 * Default props
 */
Search.defaultProps = {
  label: 'Search control',
  labelScreenReaderText: '',
  placeholder: 'Search here',
  url: '/search',
  showFilters: false,
  filters: [],
  filtersLabel: 'Filter by',
  currentFilter: null,
  searchTerm: '',
  onSubmit: null,
  submitLabel: 'Submit',
  inputId: null,
  inputName: 'search',
  srLabel: 'Search',
  context: {},
  loadStyles: true,
  extraClassNames: {},
};

export default compose(withPolaris)(Search);
